import { images } from '@/asset';
import BaseModal from '@/components/BaseModal';
import BaseTable from '@/components/BaseTable';
import BoldText from '@/components/BoldText';
import { PATH } from '@/constants/path';
import { checkShowErrorInline } from '@/helpers';
import useShippingMethodSeparator from '@/hooks/useShippingMethodSeparator';
import useToast from '@/hooks/useToast';
import useUserPlan from '@/hooks/useUserPlan';
import { apiCaller } from '@/redux/query';
import { isSkipApiSelector } from '@/redux/slice/auth.slice';
import { IPagination } from '@/types';
import { IItemCondition, ILocationCondition } from '@/types/newSetup.type';
import { Badge, Button, Icon, IndexTable, List, Text, TextField, Tooltip, useIndexResourceState } from '@shopify/polaris';
import { DeleteIcon, DuplicateIcon, EditIcon, SearchIcon } from '@shopify/polaris-icons';
import debounce from 'lodash/debounce';
import { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

interface IProps {}

interface ISearchParams {
  shippingMethods: string;
  page: number;
  perPage: number;
}

export default function Table(props: IProps) {
  const shippingMethodSeparator = useShippingMethodSeparator();
  const navigate = useNavigate();
  const { show } = useToast();
  const { isEnableCheckout } = useUserPlan();
  const isSkip = useSelector(isSkipApiSelector);

  const [searchParams, setSearchParams] = useState<ISearchParams>({
    shippingMethods: '',
    page: 1,
    perPage: 5,
  });
  const [inputValue, setInputValue] = useState('');
  const [deleteRuleSelected, setDeleteRuleSelected] = useState<number | undefined>();

  const { data: checkoutRes, isLoading } = apiCaller.useGetCheckoutRulesQuery(searchParams, { skip: isSkip });
  const [createRule, { isLoading: isLoadingDuplicate }] = apiCaller.useCreateCheckoutRuleMutation();
  const [removeRule, { isLoading: isLoadingDelete }] = apiCaller.useRemoveCheckoutRuleMutation();
  const [bulkUpdateRules] = apiCaller.useBulkUpdateCheckoutRuleMutation();
  const [bulkDeleteRules] = apiCaller.useBulkRemoveCheckoutRuleMutation();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleUpdateSearch = useCallback(
    debounce((value: string) => {
      setSearchParams((prev) => ({
        ...prev,
        shippingMethods: value,
      }));
    }, 300),
    [],
  );

  const { selectedResources, allResourcesSelected, handleSelectionChange, clearSelection } = useIndexResourceState(
    checkoutRes?.data?.map((item) => ({ ...item, id: item?.id?.toString() })) as any,
  );

  const handleEditRule = (id: number) => {
    navigate({
      ...PATH.EDIT_CHECKOUT_EXTENSION,
      pathname: `/checkout-extension/edit/${id}`,
    });
  };

  const handleOpenModalDelete = (id: number) => {
    setDeleteRuleSelected(id);
  };

  const handleRemoveCheckoutRule = async () => {
    if (!deleteRuleSelected || isSkip) return;

    try {
      const res =
        deleteRuleSelected === -1 && selectedResources.length > 0
          ? await bulkDeleteRules(selectedResources.map(Number)).unwrap()
          : await removeRule(deleteRuleSelected).unwrap();

      if (res.code === 200) {
        show(deleteRuleSelected === -1 ? 'Rules deleted' : 'Rule deleted');
      }
    } catch (error) {
      console.log(error);
    } finally {
      setDeleteRuleSelected(undefined);
      clearSelection();
    }
  };

  const handleBulkUpdate = useCallback(
    async (value: boolean) => {
      if (!selectedResources.length || isSkip) return;
      try {
        const listIds = selectedResources.map(Number);
        const res = await bulkUpdateRules({ ids: listIds, enable: value }).unwrap();
        if (res.code === 200) {
          show('Rules updated');
        }
      } catch (error) {
        console.log(error);
      } finally {
        setDeleteRuleSelected(undefined);
        clearSelection();
      }
    },
    [bulkUpdateRules, clearSelection, isSkip, selectedResources, show],
  );

  const actionItems = useMemo(() => {
    return [
      {
        content: 'Activate',
        onAction: () => {
          handleBulkUpdate(true);
        },
      },
      {
        content: 'Deactivate',
        onAction: () => {
          handleBulkUpdate(false);
        },
      },
      {
        content: <Icon source={DeleteIcon} />,
        onAction: () => {
          setDeleteRuleSelected(-1);
        },
      },
    ];
  }, [handleBulkUpdate]);

  const handleDuplicateRule = useCallback(
    async (currentId: number) => {
      const currentRule = checkoutRes?.data?.find((rule) => rule.id === currentId);
      if (!currentRule) return;
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { id, createdAt, updatedAt, ...rest } = currentRule;
      const formatCondition = (condition: IItemCondition | ILocationCondition) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { id, checkoutRuleId, value, ...newItem } = condition;
        try {
          const newValue = JSON.parse(value as unknown as string);
          return { ...newItem, value: newValue };
        } catch (error) {
          console.log(error);
          return { ...newItem };
        }
      };
      const newRule = {
        ...rest,
        checkoutItemConditions: rest.checkoutItemConditions?.map((i) => formatCondition(i)),
        checkoutLocationConditions: rest.checkoutLocationConditions?.map((i) => formatCondition(i)),
      };
      try {
        const res = await createRule({
          ...newRule,
          name: `Copy of ${currentRule?.name}`,
          shippingMethods: currentRule?.shippingMethods?.split(shippingMethodSeparator),
        }).unwrap();
        const checkError = checkShowErrorInline(res);
        if (!checkError.status) {
          show(`Rule ${id ? 'updated' : 'created'}`);
        }
      } catch (error) {
        console.log('error', error);
      }
    },
    [checkoutRes?.data, createRule, shippingMethodSeparator, show],
  );

  const rowMarkup = checkoutRes?.data?.map(
    ({ id, name = '', maxDeliveryDays, maxPreparationDays, minDeliveryDays, minPreparationDays, enable }, index) => (
      <IndexTable.Row id={id + ''} key={id} selected={selectedResources.includes(id + '')} position={index} onClick={() => {}}>
        <IndexTable.Cell>
          <Text variant="bodyMd" fontWeight="bold" as="span">
            {name}
          </Text>
        </IndexTable.Cell>
        <IndexTable.Cell>
          <Tooltip
            content={
              <List>
                <List.Item>
                  Minimum preparation days: <BoldText>{minPreparationDays}</BoldText>
                </List.Item>
                <List.Item>
                  Maximum preparation days: <BoldText>{maxPreparationDays}</BoldText>
                </List.Item>
                <List.Item>
                  Minimum delivery days: <BoldText>{minDeliveryDays}</BoldText>
                </List.Item>
                <List.Item>
                  Maximum delivery days: <BoldText>{maxDeliveryDays}</BoldText>
                </List.Item>
              </List>
            }
          >
            <div
              key={id}
              className="estimate-table-data"
            >{`${minPreparationDays}-${maxPreparationDays}-${minDeliveryDays}-${maxDeliveryDays}`}</div>
          </Tooltip>
        </IndexTable.Cell>
        <IndexTable.Cell>
          <Badge tone={enable ? 'success' : undefined}>{enable ? 'Active' : 'Inactive'}</Badge>
        </IndexTable.Cell>
        <IndexTable.Cell>
          <div className="flex-center gap-8">
            <Tooltip content="Edit">
              <Button
                disabled={!isEnableCheckout || isLoadingDuplicate}
                icon={EditIcon}
                onClick={() => handleEditRule(id)}
                variant="plain"
              />
            </Tooltip>
            <Tooltip content="Duplicate">
              <Button
                disabled={!isEnableCheckout || isLoadingDuplicate}
                icon={DuplicateIcon}
                onClick={() => {
                  handleDuplicateRule(id as number);
                }}
                variant="plain"
              />
            </Tooltip>
            <Tooltip content="Delete">
              <Button
                disabled={!isEnableCheckout || isLoadingDuplicate}
                icon={DeleteIcon}
                onClick={() => handleOpenModalDelete(id)}
                variant="plain"
              />
            </Tooltip>
          </div>
        </IndexTable.Cell>
      </IndexTable.Row>
    ),
  );

  return (
    <div>
      <div className="mt-8">
        <TextField
          value={inputValue}
          autoComplete="off"
          label=""
          labelHidden
          placeholder="Search"
          prefix={<Icon source={SearchIcon} />}
          clearButton
          onClearButtonClick={() => {
            setInputValue('');
            handleUpdateSearch('');
          }}
          onChange={(value) => {
            setInputValue(value);
            handleUpdateSearch(value);
          }}
        />
      </div>
      <div className="mt-8">
        <BaseTable
          selectable={isEnableCheckout}
          isLoading={isLoading}
          columnContentTypes={['text', 'text', 'text', 'text', 'text', 'text']}
          headings={[
            { title: 'Rule name' },
            { title: 'Estimate' },
            { title: 'Status' },
            { title: 'Actions', alignment: 'center' },
          ]}
          data={checkoutRes?.data}
          rows={rowMarkup || []}
          actionItems={actionItems}
          totalPage={checkoutRes?.meta?.pagination?.total_pages}
          totalItem={checkoutRes?.meta?.pagination?.total || 0}
          pagination={{ page: searchParams.page, perPage: searchParams.perPage }}
          setPagination={(pagination: IPagination) => {
            setSearchParams((prev) => ({ ...prev, ...pagination }));
          }}
          selectedResources={selectedResources}
          allResourcesSelected={allResourcesSelected}
          handleSelectionChange={handleSelectionChange}
          imgEmptySource={images.emptyCheckout}
          titleEmpty="No checkout shipping method rule created"
          emptyAction={() => {
            navigate(PATH.CREATE_CHECKOUT_EXTENSION);
          }}
          titleActionBtnEmpty="Create a rule"
          isSearching={!!searchParams.shippingMethods}
        />
      </div>
      <BaseModal
        open={!!deleteRuleSelected}
        onClose={() => setDeleteRuleSelected(undefined)}
        title="Delete this checkout rule?"
        loading={isLoadingDelete}
        primaryAction={{
          content: 'Delete',
          onAction: handleRemoveCheckoutRule,
          id: 'btn-destructive-tone',
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => setDeleteRuleSelected(undefined),
          },
        ]}
      >
        This checkout rule will be permanently deleted. Banners in Thank you and Order status page might be affected.
      </BaseModal>
    </div>
  );
}
